/**
 * Removes given object keys
 * Analogous to [omit](https://lodash.com/docs/4.17.15#omit) from lodash
 */
export function omit<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: ReadonlyArray<K>,
): Omit<T, K> {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key as K)),
  ) as Omit<T, K>;
}

/**
 * Preserves given object keys
 * Analogous to [pick](https://lodash.com/docs/4.17.15#pick) from lodash
 */
export function pick<T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: ReadonlyArray<K>,
): Pick<T, K> {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) =>
      (keys as readonly string[]).includes(key),
    ),
  ) as Pick<T, K>;
}

/**
 * Removes object keys for which predicate returns true
 * Analogous to [omitBy](https://lodash.com/docs/4.17.15#omitBy) from lodash
 */
export function omitBy<T extends Record<string, unknown>>(
  obj: T,
  fn: (value: T[keyof T], key: keyof T) => boolean,
): Partial<T> {
  return Object.fromEntries(
    (Object.entries(obj) as Array<[keyof T, T[keyof T]]>).filter(
      ([key, value]) => !fn(value, key),
    ),
  ) as Partial<T>;
}

/**
 * Applies a function over all object values
 * Analogous to [mapValues](https://lodash.com/docs/4.17.15#mapValues) from lodash
 */
export function mapObject<R extends Record<string, unknown>, T>(
  obj: R,
  mapper: (value: R[keyof R], key: keyof R) => T,
): Record<keyof R, T> {
  return (Object.entries(obj) as Array<[keyof R, R[keyof R]]>).reduce(
    (acc, [key, val]) => {
      acc[key] = mapper(val, key);
      return acc;
    },
    {} as Record<keyof R, T>,
  );
}

/**
 * Groups objects by specified property picked via function
 * Analogous to [groupBy](https://lodash.com/docs/4.17.15#groupBy) from lodash
 */
export function groupBy<T>(
  items: T[],
  mapper: (value: T) => string,
): Record<string, T[]> {
  return items.reduce(
    (acc, item) => {
      const key = mapper(item);

      if (!acc[key]) acc[key] = [];
      acc[key].push(item);

      return acc;
    },
    {} as Record<string, T[]>,
  );
}

/**
 * Creates a dictionary of objects by specified property picked via function
 * Analogous to [keyBy](https://lodash.com/docs/4.17.15#keyBy) from lodash
 */
export function keyBy<T>(
  items: T[],
  mapper: (value: T) => string,
): Record<string, T> {
  return items.reduce(
    (acc, item) => {
      const key = mapper(item);

      acc[key] = item;

      return acc;
    },
    {} as Record<string, T>,
  );
}

/**
 * Sorts items given numeric/string property picked via function
 */
export function sortBy<T>(
  items: T[],
  mapper: (value: T) => string | number,
): T[] {
  return [...items].sort((a, b) => {
    const valA = mapper(a);
    const valB = mapper(b);

    return valA === valB ? 0 : valA > valB ? 1 : -1;
  });
}

export function uniq<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

/**
 * Removes elements non unique by comparing computed key. Keeps first occurrences.
 * Analogous to [uniqBy](https://lodash.com/docs/4.17.15#uniqBy) from lodash
 */
export function uniqBy<T>(
  arr: T[],
  mapper: (value: T) => string | number,
): T[] {
  const seen = new Set<string | number>();

  return arr.filter((a) => {
    const key = mapper(a);
    const alreadySeen = seen.has(key);

    if (alreadySeen) return false;

    seen.add(key);

    return true;
  });
}

function isObject(obj: unknown): obj is Record<string, unknown> {
  return typeof obj === "object" && obj != null;
}

// https://gist.github.com/egardner/efd34f270cc33db67c0246e837689cb9
export function deepEqual(obj1: unknown, obj2: unknown): boolean {
  if (obj1 === obj2) {
    return true;
  } else if (isObject(obj1) && isObject(obj2)) {
    if (Object.keys(obj1).length !== Object.keys(obj2).length) {
      return false;
    }
    for (var prop in obj1) {
      if (!deepEqual(obj1[prop], obj2[prop])) {
        return false;
      }
    }
    return true;
  }

  return false;
}

export const stripHTMLTags = (text: string) => text.replace(/<[^>]*>?/gm, "");

export const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Inverts a record, swapping keys and values
 * Works with string literal types to preserve type information
 */
export function invertRecord<K extends string, V extends string>(
  record: Record<K, V>
): Record<V, K> {
  return Object.fromEntries(
    Object.entries(record).map(([k, v]) => [v, k])
  ) as Record<V, K>;
}

// https://gist.github.com/ScriptedAlchemy/25be43bfb45baaf047a7991de4b64127
export function mergeDeep<T>(...objects: [T, ...DeepPartial<T>[]]): T {
  return (objects as T[]).reduce((prev, obj) => {
    Object.keys(obj).forEach((key) => {
      const pVal = prev[key];
      const oVal = obj[key];

      if (Array.isArray(pVal) && Array.isArray(oVal)) {
        // do not concat arrays
        prev[key] = oVal;
      } else if (isObject(pVal) && isObject(oVal)) {
        prev[key] = mergeDeep(pVal, oVal);
      } else {
        prev[key] = oVal;
      }
    });

    return prev;
  }, {} as T);
}

export type DeepPartial<T> = { [P in keyof T]?: DeepPartial<T[P]> };

export function debounce<T extends (...args: any[]) => void>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId !== null) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export function capitalize(input: string): string {
  return input.charAt(0).toUpperCase() + input.slice(1);
}
